import { useQuery } from "@tanstack/react-query"

import { ukDateOf } from "@/constants/sheriffOptions"
import { supabase } from "@/lib/supabase"

import type { Enums } from "../../database.types"

interface MonthlyShiftStatsParams {
  startDate: string // ISO date string (start of month)
  endDate: string // ISO date string (end of month)
  location?: string
  employer?: string
  type?: Enums<"person_type">
  areas?: Enums<"person_areas">[]
}

export interface DayStats {
  date: string // YYYY-MM-DD
  total: number
  filled: number
  unfulfilled: number
  cancelled: number
}

const STATS_PAGE_SIZE = 1000

type StatsShift = {
  start_time: string
  status: string | null
  person_id: string | null
  type: string | null
}

/** Per-day shift counts, keyed by each shift's UK start date. */
export function buildDayStats(shifts: StatsShift[]): DayStats[] {
  const statsByDate: Record<string, DayStats> = {}

  shifts.forEach((shift) => {
    const date = ukDateOf(new Date(shift.start_time)) // The shift's UK day, as YYYY-MM-DD

    if (!statsByDate[date]) {
      statsByDate[date] = {
        date,
        total: 0,
        filled: 0,
        unfulfilled: 0,
        cancelled: 0,
      }
    }

    // Planned = Not cancelled (Status: Open or Filled) AND Not type Trial
    const isPlanned = shift.status !== "Cancelled" && shift.type !== "Trial"

    if (isPlanned) {
      statsByDate[date].total++ // total = planned shifts

      // Scheduled = Planned shifts that have people attached (not status Open)
      if (shift.person_id && shift.status !== "Open") {
        statsByDate[date].filled++ // filled = scheduled shifts
      } else {
        statsByDate[date].unfulfilled++ // unfulfilled = planned but not scheduled
      }
    }

    // Track cancelled separately (not included in planned)
    if (shift.status === "Cancelled") {
      statsByDate[date].cancelled++
    }
  })

  // Convert to array and sort by date
  return Object.values(statsByDate).sort((a, b) => a.date.localeCompare(b.date))
}

export const useMonthlyShiftStats = (params: MonthlyShiftStatsParams) => {
  return useQuery({
    queryKey: ["monthly-shift-stats", params],
    queryFn: async (): Promise<DayStats[]> => {
      // Build query - exclude Trial shifts and cancelled shifts for planned count
      const buildQuery = () => {
        let query = supabase
          .from("shifts")
          .select("start_time, status, person_id, type")
          .gte("start_time", params.startDate)
          .lte("start_time", params.endDate)

        // Apply filters
        if (params.location) {
          query = query.eq("location", params.location)
        }
        if (params.employer) {
          query = query.eq("employer", params.employer)
        }
        if (params.type) {
          query = query.eq("type", params.type)
        }
        if (params.areas && params.areas.length > 0) {
          query = query.in("area", params.areas)
        }
        return query
      }

      // Page through the month: PostgREST returns at most 1000 rows per request
      const data: StatsShift[] = []
      for (let from = 0; ; from += STATS_PAGE_SIZE) {
        const { data: page, error } = await buildQuery()
          .order("id", { ascending: true })
          .range(from, from + STATS_PAGE_SIZE - 1)

        if (error) {
          console.error("Error fetching monthly shift stats:", error)
          throw error
        }
        data.push(...(page ?? []))
        if ((page ?? []).length < STATS_PAGE_SIZE) break
      }

      return buildDayStats(data)
    },
    enabled: Boolean(params.startDate && params.endDate && params.location),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}
